import { supabase } from "../supabase";

export type NativePushPermission = "prompt" | "prompt-with-rationale" | "granted" | "denied";
export type ApnsEnvironment = "sandbox" | "production";

export const NATIVE_PUSH_TOKEN_KEY = "brain-dump:apns-device-token";
export const NATIVE_PUSH_REGISTRATION_TIMEOUT_MS = 15_000;
const APP_VERSION_TIMEOUT_MS = 2_000;
const APP_VERSION_MAX_LENGTH = 64;

/* The native bridge sets DEBUG for an Xcode Debug build, whose development entitlement mints sandbox tokens regardless of how the bundle was built. */
export const apnsEnvironment = (): ApnsEnvironment =>
  import.meta.env.DEV || window.Capacitor?.DEBUG === true ? "sandbox" : "production";

/* Wrapped because a Capacitor plugin proxy answers `then`, so resolving a promise with it bare never settles. */
const loadPlugin = async (): Promise<{
  plugin: typeof import("@capacitor/push-notifications").PushNotifications;
}> => {
  const { PushNotifications } = await import("@capacitor/push-notifications");
  return { plugin: PushNotifications };
};

export const readStoredPushToken = (): string | null =>
  localStorage.getItem(NATIVE_PUSH_TOKEN_KEY);

const storePushToken = (token: string): void =>
  localStorage.setItem(NATIVE_PUSH_TOKEN_KEY, token);

const clearStoredPushToken = (): void => localStorage.removeItem(NATIVE_PUSH_TOKEN_KEY);

export const checkNativePushPermission = async (): Promise<NativePushPermission> =>
  (await (await loadPlugin()).plugin.checkPermissions()).receive;

export const requestNativePushPermission = async (): Promise<NativePushPermission> =>
  (await (await loadPlugin()).plugin.requestPermissions()).receive;

export const registerForApnsToken = async ({
  timeoutMs = NATIVE_PUSH_REGISTRATION_TIMEOUT_MS,
}: { timeoutMs?: number } = {}): Promise<string> => {
  const { plugin: PushNotifications } = await loadPlugin();
  let resolveToken: (token: string) => void = () => undefined;
  let rejectToken: (error: Error) => void = () => undefined;
  const token = new Promise<string>((resolve, reject) => {
    resolveToken = resolve;
    rejectToken = reject;
  });
  token.catch(() => undefined);
  const timer = setTimeout(
    () => rejectToken(new Error("APNs registration timed out")),
    timeoutMs,
  );
  const listeners = [
    PushNotifications.addListener("registration", ({ value }) => resolveToken(value)),
    PushNotifications.addListener("registrationError", ({ error }) =>
      rejectToken(new Error(error)),
    ),
  ];
  let settled = false;
  try {
    Promise.all(listeners)
      .then(() => (settled ? undefined : PushNotifications.register()))
      .catch((error: unknown) =>
        rejectToken(error instanceof Error ? error : new Error(String(error))),
      );
    return await token;
  } finally {
    settled = true;
    clearTimeout(timer);
    listeners.forEach((listener) => {
      listener.then((handle) => handle.remove()).catch(() => undefined);
    });
  }
};

/* A stalled bridge must not hold the subscribe, so the version is dropped rather than waited on. */
const readAppVersion = async (): Promise<string | undefined> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const expired = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error("reading the app version timed out")), APP_VERSION_TIMEOUT_MS);
  });
  expired.catch(() => undefined);
  try {
    const { version, build } = await Promise.race([
      import("@capacitor/app").then(({ App }) => App.getInfo()),
      expired,
    ]);
    return `${version} (${build})`.slice(0, APP_VERSION_MAX_LENGTH);
  } catch {
    return undefined;
  } finally {
    clearTimeout(timer);
  }
};

const subscribeToken = async (deviceToken: string): Promise<void> => {
  const appVersion = await readAppVersion();
  const { error } = await supabase.functions.invoke("subscribe-push", {
    body: {
      kind: "apns",
      deviceToken,
      apnsEnvironment: apnsEnvironment(),
      userAgent: navigator.userAgent,
      ...(appVersion ? { appVersion } : {}),
    },
  });
  if (error) throw error;
};

const unsubscribeToken = async (deviceToken: string): Promise<void> => {
  const { error } = await supabase.functions.invoke("unsubscribe-push", {
    body: { deviceToken },
  });
  if (error) throw error;
};

const unsubscribeBestEffort = async (deviceToken: string): Promise<void> => {
  try {
    await unsubscribeToken(deviceToken);
  } catch (error) {
    console.warn("unsubscribing an APNs token failed", String(error));
  }
};

const unregisterBestEffort = async (): Promise<void> => {
  try {
    const { plugin } = await loadPlugin();
    await plugin.unregister();
  } catch (error) {
    console.warn("unregistering from APNs failed", String(error));
  }
};

/* The token is stored only once the sender has recorded it, so a stored token means this device rings. */
const registerDevice = async ({ rollbackOnFailure }: { rollbackOnFailure: boolean }): Promise<void> => {
  const deviceToken = await registerForApnsToken();
  try {
    await subscribeToken(deviceToken);
  } catch (error) {
    if (rollbackOnFailure) await unregisterBestEffort();
    throw error;
  }
  const previous = readStoredPushToken();
  storePushToken(deviceToken);
  if (previous && previous !== deviceToken) await unsubscribeBestEffort(previous);
};

export const enableNativePush = async (): Promise<NativePushPermission> => {
  const permission = await requestNativePushPermission();
  if (permission === "granted") await registerDevice({ rollbackOnFailure: true });
  return permission;
};

/* Sender first: a token the device dropped but the sender kept is pushed to until APNs rejects it. */
export const disableNativePush = async (): Promise<void> => {
  const deviceToken = readStoredPushToken();
  if (deviceToken) await unsubscribeToken(deviceToken);
  await unregisterBestEffort();
  clearStoredPushToken();
};

/* Called while the session is still valid, so the unsubscribe is authenticated; the device is released even if it fails. */
export const releaseNativePushOnSignOut = async (): Promise<void> => {
  const deviceToken = readStoredPushToken();
  if (!deviceToken) return;
  await unsubscribeBestEffort(deviceToken);
  await unregisterBestEffort();
  clearStoredPushToken();
};

/* APNs tokens rotate, so a device the user switched on re-registers and re-subscribes on every launch and resume. */
export const reconcileNativePush = async (): Promise<void> => {
  const deviceToken = readStoredPushToken();
  if (!deviceToken) return;
  const permission = await checkNativePushPermission();
  if (permission === "granted") {
    await registerDevice({ rollbackOnFailure: false });
    return;
  }
  await unsubscribeBestEffort(deviceToken);
  await unregisterBestEffort();
  clearStoredPushToken();
};

export const addNativePushTapListener = async ({
  onTap,
}: {
  onTap: (url: string | null) => void;
}): Promise<() => Promise<void>> => {
  const { plugin } = await loadPlugin();
  const handle = await plugin.addListener("pushNotificationActionPerformed", ({ notification }) => {
    const data: unknown = notification.data;
    const url = typeof data === "object" && data !== null && "url" in data ? data.url : null;
    onTap(typeof url === "string" ? url : null);
  });
  return () => handle.remove();
};
